/**
 * useOrgUsers — TanStack Query read over
 * `GET /organizations/:slug/users`, exposed as an owner-name resolver
 * for the Owner tab (SCRUM-172, Decision 1).
 *
 * `OwnershipResponseDto` carries the owner as a `userId` (UUID), not a
 * display name. Rather than an N+1 of `GET /users/:userId`, the Owner
 * tab loads the org member list once and resolves `userId → name`
 * client-side. Resolution is **best-effort**: the hook exposes
 * `resolveOwnerName(userId)` returning `undefined` when the user isn't
 * in the loaded page, and the ownership mapper falls back to the raw
 * `userId` — honest, never invented.
 *
 * Display name = `firstName + lastName` (both nullable for pre-
 * invitation-name users) → falls back to `email`. The hook requests a
 * generous page size so a typical org's members resolve in one fetch;
 * pagination beyond the first page is a follow-up if large orgs need
 * it (the userId fallback keeps the UI correct meanwhile).
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { components } from '@artemis/contracts';
import type { OwnerNameResolver } from './ownership-mapper.js';

type OrgMember = components['schemas']['OrgMemberListResponseDto'];

/**
 * Largest page the BE accepts in one shot for best-effort resolution.
 * The users list endpoint caps `per_page` at 100 (default 50, max 100 —
 * `users.controller.ts`); requesting more is rejected with a 422. This is
 * a best-effort name resolver, so one page of 100 is the ceiling we ask
 * for (org member lists beyond 100 fall back to the userId display).
 */
const RESOLVE_PAGE_SIZE = 100;

function memberDisplayName(member: OrgMember): string {
  const full = [member.firstName, member.lastName]
    .filter((part): part is string => part !== null && part !== undefined && part.length > 0)
    .join(' ');
  return full.length > 0 ? full : member.email;
}

/** A selectable org member for the owner pickers (open / transfer). */
export interface OrgUserOption {
  readonly id: string;
  readonly name: string;
}

export interface UseOrgUsersResult {
  /** True while the member list is loading (resolution falls back to userId meanwhile). */
  readonly isLoading: boolean;
  /** Resolver for the ownership mapper — `undefined` when unresolved. */
  readonly resolveOwnerName: OwnerNameResolver;
  /** Members for the owner pickers, sorted by display name. */
  readonly members: readonly OrgUserOption[];
}

export function useOrgUsers(): UseOrgUsersResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: ['org-users', orgSlug] as const,
    enabled: orgSlug !== null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET('/organizations/{slug}/users', {
        params: {
          path: { slug: orgSlug as string },
          query: { per_page: RESOLVE_PAGE_SIZE },
        },
      });
      if (!result.response.ok || result.data === undefined) {
        // Resolution is best-effort — a failed lookup is non-fatal;
        // the mapper falls back to userId. Return an empty map.
        return new Map<string, string>();
      }
      const map = new Map<string, string>();
      for (const member of result.data.data) {
        map.set(member.id, memberDisplayName(member));
      }
      return map;
    },
  });

  const nameMap = query.data ?? null;
  const resolveOwnerName: OwnerNameResolver = (userId) =>
    nameMap?.get(userId);

  const members: readonly OrgUserOption[] =
    nameMap === null
      ? []
      : Array.from(nameMap, ([id, name]) => ({ id, name })).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

  return {
    isLoading: orgSlug !== null && query.isPending,
    resolveOwnerName,
    members,
  };
}
